import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface ElderCareStatus {
  elderlyCount: number;
  elderlyPct: number;
  disabledCount: number;
  vhvCount: number;
  residentsPerVhv: number;
  hospitalKm: number;
  healthStationCount: number;
  highIsolationHouseholds: number;
  checkInCoverage: "good" | "partial" | "limited";
  alert: string | null;
  source: string;
  observedAt: string;
}

interface Props {
  data: ElderCareStatus | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const COVERAGE_COLOR: Record<ElderCareStatus["checkInCoverage"], string> = {
  good: "var(--good)",
  partial: "var(--warn)",
  limited: "var(--bad)",
};

const COVERAGE_LABEL: Record<ElderCareStatus["checkInCoverage"], string> = {
  good: "ดี",
  partial: "บางส่วน",
  limited: "ไม่เพียงพอ",
};

export function ElderCarePanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <div className="eyebrow">ผู้สูงอายุ / ELDER CARE</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!data) return null;

  const coverageColor = COVERAGE_COLOR[data.checkInCoverage];
  const coverageLabel = COVERAGE_LABEL[data.checkInCoverage];

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="ผู้สูงอายุ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="dopa·dhss"
      />

      {data.alert && (
        <div style={{
          background: "var(--bad)",
          color: "var(--fixed-light)",
          fontSize: "var(--size-eyebrow)",
          padding: "4px 6px",
          marginBottom: 4,
        }}>
          ⚠ {data.alert}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", alignItems: "baseline" }}>
        <span className="caption" style={{ color: "var(--text-2)" }}>ผู้สูงอายุ 60+</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {data.elderlyCount.toLocaleString()} คน
          <span style={{ color: "var(--text-3)", marginLeft: 4 }}>({data.elderlyPct}%)</span>
        </span>

        <span className="caption" style={{ color: "var(--text-2)" }}>ผู้พิการ</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{data.disabledCount.toLocaleString()} คน</span>

        <span className="caption" style={{ color: "var(--text-2)" }}>อสม. ในพื้นที่</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{data.vhvCount} คน</span>

        <span className="caption" style={{ color: "var(--text-2)" }}>สัดส่วนประชากร/อสม.</span>
        <span className="mono caption" style={{ textAlign: "right" }}>1 : {data.residentsPerVhv}</span>

        <span className="caption" style={{ color: "var(--text-2)" }}>ครัวเรือนเสี่ยงสูง</span>
        <span className="mono caption" style={{ textAlign: "right", color: "var(--warn)" }}>
          ~{data.highIsolationHouseholds} หลัง
        </span>

        <span className="caption" style={{ color: "var(--text-2)" }}>โรงพยาบาล</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{data.hospitalKm} กม.</span>

        <span className="caption" style={{ color: "var(--text-2)" }}>รพ.สต. ในพื้นที่</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{data.healthStationCount} แห่ง</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        <span className="eyebrow" style={{ color: "var(--text-3)" }}>ความครอบคลุม อสม.</span>
        <span className="eyebrow mono" style={{ marginLeft: "auto", color: coverageColor }}>
          {coverageLabel}
        </span>
      </div>

      <div className="caption" style={{ color: "var(--text-3)", marginTop: 2, fontSize: "var(--size-eyebrow)" }}>
        ครัวเรือนเสี่ยงสูง = ผู้สูงอายุ 70+ อยู่ลำพัง (ประมาณการ)
      </div>
    </div>
  );
}
